import { useCallback, useEffect, useState } from "react";
import { GameCard } from "../components/GameCard";
import { Sheet } from "../components/Sheet";
import { Toast } from "../components/Toast";
import { espnLabel, searchEspnGames, type EspnGame } from "../lib/addEvent";
import {
  RSVP_CHOICES,
  RSVP_FILTERS,
  addFromEspn,
  addManual,
  byMonth,
  deleteUpcoming,
  fetchSchedule,
  fetchTeams,
  fetchUpcoming,
  setRsvp,
  updateUpcoming,
  type RsvpFilter,
  type Team,
  type UpcomingDto,
  type UpcomingRow,
} from "../lib/upcomingPage";

const input = "min-w-0 rounded-lg border border-line bg-surface px-2.5 py-2 text-[15px] text-neutral-100";
const labelCls = "grid gap-1 text-xs text-muted";
const RSVP_ON: Record<string, string> = {
  yes: "border-good bg-good text-ground font-semibold",
  maybe: "border-soon bg-soon text-ground font-semibold",
  no: "border-bad bg-bad text-ground font-semibold",
};

function Text({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <label className={labelCls}>
      {label}
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={input} />
    </label>
  );
}

function EditSheet({ g, onClose, onDone }: { g: UpcomingRow; onClose: () => void; onDone: (m: string) => void }) {
  const [e, setE] = useState({
    event_date: g.event_date,
    home_team: g.home_team,
    away_team: g.away_team,
    venue_name: g.venue_name ?? "",
    venue_city: g.venue_city ?? "",
    url: g.url ?? "",
    notes: g.notes ?? "",
  });
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = (p: Promise<unknown>, m: string) => p.then(() => onDone(m)).catch((x: unknown) => setError(x instanceof Error ? x.message : String(x)));
  return (
    <Sheet title="Edit game" onClose={onClose}>
      <div className="grid gap-2.5">
        <Text label="Date" type="date" value={e.event_date} onChange={(v) => setE({ ...e, event_date: v })} />
        <div className="grid grid-cols-2 gap-2.5">
          <Text label="Home team" value={e.home_team} onChange={(v) => setE({ ...e, home_team: v })} />
          <Text label="Away team" value={e.away_team} onChange={(v) => setE({ ...e, away_team: v })} />
          <Text label="Venue" value={e.venue_name} onChange={(v) => setE({ ...e, venue_name: v })} />
          <Text label="City" value={e.venue_city} onChange={(v) => setE({ ...e, venue_city: v })} />
        </div>
        <Text label="Ticket URL" value={e.url} onChange={(v) => setE({ ...e, url: v })} />
        <Text label="Notes" value={e.notes} onChange={(v) => setE({ ...e, notes: v })} />
        {error && <p className="text-sm text-bad">{error}</p>}
        {confirm ? (
          <div className="flex items-center justify-end gap-2">
            <span className="mr-auto text-sm">Remove this game from the watchlist?</span>
            <button type="button" onClick={() => setConfirm(false)} className="min-h-10 rounded-lg border border-line px-3.5 text-sm">
              Keep
            </button>
            <button type="button" onClick={() => run(deleteUpcoming(g.id), "Removed")} className="min-h-10 rounded-lg bg-bad px-3.5 text-sm font-semibold text-ground">
              Yes, remove
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setConfirm(true)} className="mr-auto min-h-10 rounded-lg border border-line px-3.5 text-sm text-bad">
              Remove
            </button>
            <button
              type="button"
              disabled={g.rsvp === null}
              onClick={() => run(setRsvp(g.id, null), "RSVP cleared")}
              className="min-h-10 rounded-lg border border-line px-3.5 text-sm disabled:opacity-40"
            >
              Clear RSVP
            </button>
            <button type="button" onClick={() => run(updateUpcoming(g.id, e), "Saved")} className="min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground">
              Save
            </button>
          </div>
        )}
      </div>
    </Sheet>
  );
}

function AddSheet({ dto, onClose, onDone }: { dto: UpcomingDto; onClose: () => void; onDone: (m: string) => void }) {
  const [league, setLeague] = useState(dto.espn_leagues.includes("NHL") ? "NHL" : (dto.espn_leagues[0] ?? ""));
  const [mode, setMode] = useState<"team" | "date">("team");
  const [teams, setTeams] = useState<Team[]>([]);
  const [team, setTeam] = useState("");
  const [date, setDate] = useState(dto.since);
  const [found, setFound] = useState<EspnGame[] | null>(null);
  const [futureOnly, setFutureOnly] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [manual, setManual] = useState(false);
  const [m, setM] = useState({ event_date: dto.since, league: "", home_team: "", away_team: "", venue_name: "", venue_city: "", game_type: "Regular Season", url: "", notes: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!league) return;
    let cancelled = false;
    fetchTeams(league)
      .then((t) => {
        if (cancelled) return;
        setTeams(t);
        setTeam(t[0]?.espn_team_id ?? "");
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [league]);

  async function search() {
    setBusy(true);
    setError(null);
    try {
      setFound(mode === "team" ? await fetchSchedule(league, team) : await searchEspnGames(league, date));
      setPicked(new Set());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setFound([]);
    } finally {
      setBusy(false);
    }
  }

  const shown = (found ?? []).filter((g) => !futureOnly || g.event_date >= dto.since);
  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };

  return (
    <Sheet title="Add upcoming game" onClose={onClose} wide>
      <div className="grid gap-3">
        <div className="grid grid-cols-2 gap-2.5">
          <label className={labelCls}>
            League
            <select value={league} onChange={(e) => { setLeague(e.target.value); setFound(null); }} className={input}>
              {dto.espn_leagues.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </label>
          <div role="group" aria-label="Search by" className="grid grid-cols-2 gap-1 self-end">
            {(["team", "date"] as const).map((x) => (
              <button key={x} type="button" aria-pressed={mode === x} onClick={() => { setMode(x); setFound(null); }} className={`min-h-10 rounded-md border text-sm ${mode === x ? "border-faint bg-raised" : "border-line text-muted"}`}>
                {x === "team" ? "Team schedule" : "Date"}
              </button>
            ))}
          </div>
          {mode === "team" ? (
            <label className={`${labelCls} col-span-2`}>
              Team
              <select value={team} onChange={(e) => setTeam(e.target.value)} className={input}>
                {teams.map((t) => (
                  <option key={t.espn_team_id} value={t.espn_team_id}>
                    {t.display_name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <Text label="Date" type="date" value={date} onChange={setDate} />
          )}
        </div>
        <button type="button" disabled={busy || (mode === "team" && !team)} onClick={search} className="justify-self-start rounded-lg border border-line px-3.5 py-2 text-sm">
          {mode === "team" ? "Search schedule" : "Search games"}
        </button>
        {found && (
          <>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={futureOnly} onChange={(e) => setFutureOnly(e.target.checked)} />
              Only games from today forward
            </label>
            {shown.length === 0 && <p className="text-sm text-muted">No games to show.</p>}
            <div className="grid max-h-72 gap-1.5 overflow-y-auto">
              {shown.map((g) => (
                <button key={g.espn_event_id} type="button" aria-pressed={picked.has(g.espn_event_id)} onClick={() => toggle(g.espn_event_id)} className={`rounded-lg border px-3 py-2 text-left text-sm ${picked.has(g.espn_event_id) ? "border-accent bg-accent/10" : "border-line"}`}>
                  {g.event_date}
                  {g.start_time ? ` · ${g.start_time}` : ""} · {espnLabel(g)}
                </button>
              ))}
            </div>
          </>
        )}
        <button type="button" onClick={() => setManual(!manual)} className="justify-self-start text-sm text-accent">
          {manual ? "Hide manual entry" : "League ESPN doesn't cover? Add it by hand"}
        </button>
        {manual && (
          <div className="grid grid-cols-2 gap-2.5">
            <Text label="Date" type="date" value={m.event_date} onChange={(v) => setM({ ...m, event_date: v })} />
            <Text label="League" value={m.league} onChange={(v) => setM({ ...m, league: v })} />
            <Text label="Home team" value={m.home_team} onChange={(v) => setM({ ...m, home_team: v })} />
            <Text label="Away team" value={m.away_team} onChange={(v) => setM({ ...m, away_team: v })} />
            <Text label="Venue" value={m.venue_name} onChange={(v) => setM({ ...m, venue_name: v })} />
            <Text label="City" value={m.venue_city} onChange={(v) => setM({ ...m, venue_city: v })} />
            <label className={labelCls}>
              Game type
              <select value={m.game_type} onChange={(e) => setM({ ...m, game_type: e.target.value })} className={input}>
                {dto.game_types.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </label>
            <Text label="Ticket URL (optional)" value={m.url} onChange={(v) => setM({ ...m, url: v })} />
          </div>
        )}
        {error && <p className="text-sm text-bad">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-10 rounded-lg border border-line px-3.5 text-sm">
            Cancel
          </button>
          {manual ? (
            <button
              type="button"
              onClick={() => {
                if (!m.home_team.trim() || !m.away_team.trim()) return setError("Home and away team are required.");
                if (!m.league.trim()) return setError("League is required.");
                addManual(m).then(() => onDone("Added")).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
              }}
              className="min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground"
            >
              Add to upcoming
            </button>
          ) : (
            <button
              type="button"
              disabled={picked.size === 0 || busy}
              onClick={() =>
                addFromEspn(league, [...picked])
                  .then(() => onDone(`Added ${picked.size} ${picked.size === 1 ? "game" : "games"}`))
                  .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
              }
              className="min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground disabled:opacity-40"
            >
              Add {picked.size || ""} {picked.size === 1 ? "game" : "games"}
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}

/**
 * Upcoming: the watchlist by month, with the RSVP as three buttons on every
 * game. Edit, Clear RSVP and Remove sit behind ⋯ (mockup, 2026-09-10).
 */
export default function Upcoming() {
  const [rsvp, setRsvpFilter] = useState<RsvpFilter>("all");
  const [past, setPast] = useState(false);
  const [dto, setDto] = useState<UpcomingDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const [sheet, setSheet] = useState<"add" | number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const reload = useCallback(() => setReloads((n) => n + 1), []);
  const closeToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    let cancelled = false;
    fetchUpcoming(rsvp, past)
      .then((d) => {
        if (cancelled) return;
        setDto(d);
        setError(null);
      })
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [rsvp, past, reloads]);

  if (error && !dto) return <div className="rounded-lg border border-bad/50 bg-bad/10 p-4 text-sm">Could not load the watchlist: {error}</div>;
  if (!dto) return <p className="text-sm text-muted">Loading…</p>;
  const editing = typeof sheet === "number" ? dto.games.find((g) => g.id === sheet) : undefined;
  const done = (m: string) => {
    setSheet(null);
    setToast(m);
    reload();
  };

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h1 className="font-display text-3xl leading-none font-bold uppercase">Upcoming</h1>
        <span className="text-sm text-muted">{dto.games.length} on the watchlist</span>
        <button type="button" onClick={() => setSheet("add")} className="ml-auto min-h-10 rounded-lg bg-neutral-100 px-3.5 text-sm font-semibold text-ground">
          ＋ Add game
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {RSVP_FILTERS.map((f) => (
          <button key={f.value} type="button" aria-pressed={rsvp === f.value} onClick={() => setRsvpFilter(f.value)} className={`rounded-full border px-3 py-1 text-[13px] ${rsvp === f.value ? "border-neutral-100 bg-neutral-100 font-semibold text-ground" : "border-line text-neutral-300"}`}>
            {f.label}
          </button>
        ))}
        <button type="button" aria-pressed={past} onClick={() => setPast(!past)} className={`rounded-full border px-3 py-1 text-[13px] ${past ? "border-neutral-100 bg-neutral-100 font-semibold text-ground" : "border-line text-neutral-300"}`}>
          Past 60 days
        </button>
      </div>
      {dto.games.length === 0 && <p className="text-sm text-muted">Nothing here yet. Use Add game to search ESPN and put a game on the list.</p>}
      {byMonth(dto.games).map((m) => (
        <section key={m.month} className="grid gap-2.5">
          <h2 className="font-display text-2xl leading-none font-bold">{m.month}</h2>
          <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
            {m.games.map((g) => (
              <GameCard
                key={g.id}
                game={g}
                logos={dto.logos}
                venueImages={dto.venue_images}
                extra={
                  (g.start_time || g.url || g.is_logged || g.notes) && (
                    <div className="flex flex-wrap items-center gap-2.5 text-[13px]">
                      {g.start_time && <span className="font-semibold text-accent">{g.start_time}</span>}
                      {g.url && (
                        <a href={g.url} target="_blank" rel="noreferrer" className="text-accent underline-offset-2 hover:underline">
                          🎫 Tickets
                        </a>
                      )}
                      {g.is_logged && <span className="text-good">✓ Logged</span>}
                      {g.notes && <span className="text-muted">{g.notes}</span>}
                    </div>
                  )
                }
                footer={
                  <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-1.5">
                    {RSVP_CHOICES.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        aria-pressed={g.rsvp === c.value}
                        onClick={() => setRsvp(g.id, g.rsvp === c.value ? null : c.value).then(reload).catch((e: unknown) => setToast(e instanceof Error ? e.message : String(e)))}
                        className={`min-h-10 rounded-lg border text-[13.5px] ${g.rsvp === c.value ? RSVP_ON[c.value] : "border-line text-muted"}`}
                      >
                        {c.label}
                      </button>
                    ))}
                    <button type="button" aria-label={`Edit ${g.away_team} at ${g.home_team}`} onClick={() => setSheet(g.id)} className="w-10 text-lg text-faint hover:text-neutral-100">
                      ⋯
                    </button>
                  </div>
                }
              />
            ))}
          </div>
        </section>
      ))}
      {sheet === "add" && <AddSheet dto={dto} onClose={() => setSheet(null)} onDone={done} />}
      {editing && <EditSheet g={editing} onClose={() => setSheet(null)} onDone={done} />}
      {toast && <Toast text={toast} onDone={closeToast} />}
    </section>
  );
}
